import {
	memo,
	useEffect,
	useState,
} from 'react'
import length from 'ramda/src/length'
import dropCardConnector from 'root/src/client/web/list/listItemComponents/connectors/dropCardConnector'
import withModuleContext from 'root/src/client/util/withModuleContext'
import { DareCard } from 'root/src/client/web/list/listItemComponents/dare/DareCard'
import TooltipWrapper from 'root/src/client/web/base/TooltipWraper'
import {
	GET_STARTED_MODULE_ID,
} from 'root/src/shared/descriptions/modules/moduleIds'
import getBountyName from 'root/src/shared/util/getBountyName'
import { COMING_SOON_MASK_TEXT, RewardForValue } from 'root/src/shared/constants/dropEvent'
import { projectAcceptedKey } from 'root/src/shared/constants/keys'
import { DareContextProvider } from 'root/src/client/web/list/listItemComponents/dare/contexts/dare'
import { useAcceptDare } from 'root/src/client/v2/drop/hooks/useAcceptDare'
import { useTour } from '@reactour/tour'
import { DROP_ONBOARDING_INTERACTED_STEPS } from 'root/src/client/web/record/DropEvent/onboarding'
import { trpc } from 'root/src/client/v2/trpc/trpc'

const DareCardDrop = ({
	dare, isAuthenticated, campaignId, canAccept, submitting, setSubmitting,
	platformId, platform, setInput, dropEvent, recordId,
	mnemonicId, redirectToCreateBrandProjectForm, displayModal, comingSoon,
	toggleEmailSubscription, emailSubscription, canAccessCreateBrandProject,
	userData, userId, showTermsModal, pointsToEarn, canEarnPoints,
	tokens, creatorCampaignPointsLimit, userCampaignData, getUserDropEventLandingShortUrl,
	followerCountByPlatforms, displayHowToEarnPointsModal, fetchUserCampaignData, tooltipClassName, dareCardClassName, refetchDropEvent, ...props
}) => {
	const [error, setError] = useState('')
	const { onAcceptDare } = useAcceptDare({ drop: dropEvent, dare })

	const queryUtils = trpc.useUtils()

	const handleCreatorOnboarding = async () => {
		const creatorOnboardingState = await queryUtils.creators.getCreatorOnboardingState.fetch()
		if (creatorOnboardingState?.hasAcceptedDare) {
			return
		}
		queryUtils.creators.getCreatorOnboardingState.setData(undefined, () => ({
			...creatorOnboardingState,
			hasAcceptedDare: true,
		}))
	}

	const tour = useTour()
	const onClick = async (setHover) => {
		if (!isAuthenticated) {
			displayModal(GET_STARTED_MODULE_ID)
			return
		}

		if (!canAccessCreateBrandProject && comingSoon) {
			if (!emailSubscription) {
				toggleEmailSubscription(setError)
			}
			return
		}

		setHover(true)
		try {
			await onAcceptDare({
				onSuccess: () => {
					tour.setCurrentStep(4)
					tour.setSteps(DROP_ONBOARDING_INTERACTED_STEPS(dropEvent))
					void handleCreatorOnboarding()
				},
			})
			await fetchUserCampaignData(dropEvent.mnemonicId)
		} catch (err) {
			console.error(err)
		}
		setHover(false)
	}

	useEffect(() => {
		let timeout
		if (error) {
			timeout = setTimeout(() => {
				setError('')
			}, 5000)
		}
		return () => {
			clearTimeout(timeout)
		}
	}, [error])

	const onEmailSubscribe = async () => {
		await toggleEmailSubscription(setError)
	}

	const { product } = dare
	const { name, type, platform: productPlatform, region } = product
	const brandProductName = comingSoon
		? COMING_SOON_MASK_TEXT
		: getBountyName({
			name,
			type,
			comingSoon,
			region,
			platform: productPlatform,
		}, dare?.rewardFor === RewardForValue.FANS)
	return (
		<TooltipWrapper tooltipText={error} className={tooltipClassName} visible>
			<DareContextProvider value={{
				comingSoon,
				emailSubscription,
				onClick,
				onEmailSubscribe,
				campaignId,
				brandProduct: { brandProductName, type },
				projectAccepted: dare.status === projectAcceptedKey,
				projectCompleted: length(dare.deliveries) > 0,
				projectDeliveryPending: dare.isDeliveryPending,
				projectStatus: dare.status,
				pointsToEarn,
				canEarnPoints,
				tokens,
				darePlatform: dare.platform,
				brand: dropEvent.brand,
				canAccessCreateBrandProject,
				dare,
				isLockedByTier: isLockedByTier({ dare, userCampaignData }),
				...props,
			}}
			>
				<DareCard className={dareCardClassName} />
			</DareContextProvider>
		</TooltipWrapper>
	)
}

const isLockedByTier = ({ dare, userCampaignData }) => {
	const { battlePass } = dare
	if (!battlePass) {
		return false
	}
	const userTier = userCampaignData?.creatorBattlePassProgress?.[battlePass.id] || 0
	return battlePass.tier - 1 > userTier
}

export default withModuleContext(
	dropCardConnector(memo(DareCardDrop)),
)
